/**
 * CapacityRuleCard — one named capacity rule occupying one SLOT.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Collapsed it is a status line: the name and the live "N / max" meter, which
 * is the only thing anyone reads 99% of the time. The controls (name, slot,
 * min/max) live behind the row's own disclosure, because a cap is set once and
 * then watched for weeks. Over-cap tints the row so a problem still finds you
 * with everything closed.
 */

import { FC, useId, useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import type { CapacityRule, CapacitySlot } from '@trafficpulse/shared';

import { Input, Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import CapacityBoundsRow from './CapacityBoundsRow';
import CapacityScopeChips from './CapacityScopeChips';
import { CapacityRuleMeters, type RuleGroupMeter } from './meters';
import {
  capacityBoundsError,
  makeCapacityRule,
  SLOT_LABEL,
  slotOfRule,
  type SlotActiveCounts,
} from './capacity-rules';

export type { RuleGroupMeter };

export interface CapacityRuleCardProps {
  rule: CapacityRule;
  counts: SlotActiveCounts;
  /** Rule-paths groups (name + active) for the per-group meters. */
  ruleGroups: RuleGroupMeter[];
  /** Slots already capped by other rules (locked in this rule's chips). */
  disabledSlots?: ReadonlyArray<CapacitySlot>;
  onChange: (next: CapacityRule) => void;
  onRemove: () => void;
}

const CapacityRuleCard: FC<CapacityRuleCardProps> = ({
  rule,
  counts,
  ruleGroups,
  disabledSlots = [],
  onChange,
  onRemove,
}) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const patch = (next: Partial<CapacityRule>) => onChange({ ...rule, ...next });
  const slot = slotOfRule(rule);
  const setSlot = (next: CapacitySlot) => {
    // Re-seed layers/pathScope for the new slot, keep the user's name + bounds.
    const seeded = makeCapacityRule(next);
    onChange({
      ...seeded,
      id: rule.id,
      name: rule.name,
      max: rule.max,
      ...(typeof rule.min === 'number' ? { min: rule.min } : {}),
      ...(rule.fillToMax ? { fillToMax: true } : {}),
    });
  };
  const boundsError = capacityBoundsError(rule);

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex items-center gap-2 p-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={`Edit ${rule.name || SLOT_LABEL[slot]}`}
          className="flex flex-1 items-center gap-2.5 rounded text-left"
        >
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden
          />
          <span className="shrink-0 text-[12px] font-medium">
            {rule.name || SLOT_LABEL[slot]}
          </span>
          <span className="flex flex-wrap items-start gap-x-4 gap-y-1">
            <CapacityRuleMeters
              rule={rule}
              slot={slot}
              counts={counts}
              ruleGroups={ruleGroups}
            />
          </span>
        </button>
        <Tooltip content="Remove capacity rule">
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove capacity rule"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </Tooltip>
      </div>
      {boundsError ? (
        <p role="alert" className="px-2 pb-2 text-[11px] font-medium text-destructive">
          {boundsError}
        </p>
      ) : null}
      {open ? (
        <div id={panelId} className="space-y-2 border-t border-border/60 p-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Input
              value={rule.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="Rule name"
              className="h-7 w-40 text-xs"
              aria-label="Capacity rule name"
            />
            <CapacityScopeChips
              value={slot}
              disabledSlots={disabledSlots}
              onChange={setSlot}
            />
            <CapacityBoundsRow rule={rule} slot={slot} onPatch={patch} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CapacityRuleCard;
